// File: src/app/AuthContext.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
// All rights reserved.
//
// Authentication context for admin portal

import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebaseConfig';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';

const AuthContext = createContext(null);

// E2E Test Auth Support
const TEST_AUTH_SECRET = process.env.REACT_APP_TEST_AUTH_SECRET;
const IS_DEV = process.env.NODE_ENV === 'development';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      // E2E Guard: If we have a mock session, ignore "null" from real Firebase
      if (IS_DEV && TEST_AUTH_SECRET && !firebaseUser && localStorage.getItem('firebase-auth-mock')) {
        console.log('[MockAuth] Ignoring Firebase null state due to active mock session');
        return;
      }
      setUser(firebaseUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // E2E Mock Auth Helper (Dev only)
  useEffect(() => {
    if (IS_DEV && TEST_AUTH_SECRET) {
      // Restore from localStorage on mount if no user yet
      const storedMock = localStorage.getItem('firebase-auth-mock');
      if (storedMock && !user) {
        try {
          const parsed = JSON.parse(storedMock);
          if (parsed.isAuthenticated && parsed.user) {
            console.log('[MockAuth] Restoring session for:', parsed.user.email);
            const mockToken = `${TEST_AUTH_SECRET}:${parsed.user.uid}:${parsed.user.email}`;
            const restoredUser = {
              ...parsed.user,
              emailVerified: true,
              getIdToken: async () => mockToken,
              reload: async () => {},
              delete: async () => {
                localStorage.removeItem('firebase-auth-mock');
                setUser(null);
              }
            };
            setUser(restoredUser);
            setLoading(false);
          }
        } catch (e) {
          console.error('[MockAuth] Invalid stored session', e);
        }
      }

      // Expose global mock sign-in for Playwright
      window.mockAdminSignIn = async (uid, email) => {
        console.log('[MockAuth] Signing in as:', uid, email);
        const mockToken = `${TEST_AUTH_SECRET}:${uid}:${email}`;
        const mockUser = {
          uid,
          email,
          displayName: `Admin Test ${uid.substring(0, 5)}`,
          emailVerified: true,
          photoURL: null,
          getIdToken: async () => mockToken,
          reload: async () => {},
          delete: async () => {
            localStorage.removeItem('firebase-auth-mock');
            setUser(null);
          }
        };

        // Persist to localStorage for page reloads
        localStorage.setItem('firebase-auth-mock', JSON.stringify({
          user: { uid, email, displayName: mockUser.displayName, photoURL: null },
          isAuthenticated: true
        }));

        setUser(mockUser);
        setLoading(false);
      };
    }
  }, [user]);

  const signOut = async () => {
    try {
      // E2E Mock Cleanup
      if (IS_DEV && TEST_AUTH_SECRET) {
        localStorage.removeItem('firebase-auth-mock');
      }
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { signInWithPopup } = await import('firebase/auth');
      const { googleProvider } = await import('../firebaseConfig');
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error("Error signing in with Google", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signOut, signInWithGoogle }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}



